/**
 * ForgotPassword.js
 * Request a password reset link for the Procurement Intelligence Platform
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';

const colors = {
  deepBlue: '#051C2C',
  electricBlue: '#2251FF',
  cyan: '#00A9F4',
  white: '#FFFFFF',
  lightGray: '#F5F7FA',
  mediumGray: '#E1E5EB',
  textMuted: '#8A9BAE',
  darkText: '#1A2B3C',
  danger: '#DC2626',
  success: '#059669',
};

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        throw new Error('Something went wrong. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${colors.deepBlue} 0%, #0a2a4a 50%, ${colors.electricBlue}40 100%)`,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.white,
        borderRadius: '16px',
        boxShadow: 'var(--shadow-lg)',
        width: '100%',
        maxWidth: '420px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1 style={{
            color: colors.white,
            fontSize: 'var(--font-size-hero)',
            fontWeight: 'var(--font-weight-bold)',
            margin: '0 0 6px 0',
          }}>
            Reset Password
          </h1>
          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: 'var(--font-size-base)',
            margin: 0,
          }}>
            Enter your email to receive a reset link
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '32px' }}>
          {submitted ? (
            <div>
              <div style={{
                backgroundColor: `${colors.success}15`,
                border: `1px solid ${colors.success}30`,
                color: colors.success,
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: 'var(--font-size-base)',
                lineHeight: 1.6,
              }}>
                If that email is registered, you'll receive a reset link shortly. Check your inbox.
              </div>
              <Link
                to="/login"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  color: colors.electricBlue,
                  fontWeight: '500',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-base)',
                }}
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  backgroundColor: `${colors.danger}15`,
                  border: `1px solid ${colors.danger}30`,
                  color: colors.danger,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  fontSize: 'var(--font-size-base)',
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'var(--font-weight-medium)',
                  fontSize: 'var(--font-size-base)',
                  color: colors.darkText,
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  disabled={loading}
                  autoComplete="email"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: `2px solid ${colors.mediumGray}`,
                    borderRadius: '12px',
                    fontSize: 'var(--font-size-base)',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.electricBlue}
                  onBlur={(e) => e.target.style.borderColor = colors.mediumGray}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: `linear-gradient(135deg, ${colors.electricBlue}, ${colors.cyan})`,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: 'var(--font-size-md)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = `0 4px 12px ${colors.electricBlue}50`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          backgroundColor: colors.lightGray,
          borderTop: `1px solid ${colors.mediumGray}`,
        }}>
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
            Remember your password?{' '}
            <Link
              to="/login"
              style={{ color: colors.electricBlue, fontWeight: '500', textDecoration: 'none' }}
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
